import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReceptionDifference } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate } from '../lib/format';
import type { Language } from '../i18n';

const STATES = ['OPEN', 'RESOLVED', ''] as const;

/** Diferencias de recepción para Gerencia: faltantes, averías y sobrantes por regularizar (Etapa 2). */
export function Differences() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [status, setStatus] = useState<string>('OPEN');
  const differences = useLoad(() => api.receptionDifferences({ status: status || undefined }), [status]);
  const [target, setTarget] = useState<ReceptionDifference | null>(null);
  const [reason, setReason] = useState('');

  const rows = differences.data ?? [];

  const resolve = (accept: boolean) => (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    const scope = `diferencia:${target.id}`;
    void run(async () => {
      try {
        const r = await api.resolveSurplus(requestKey(scope), target.id, { accept, reason });
        releaseKey(scope);
        setTarget(null); setReason('');
        show(t(accept ? 'differences.accepted' : 'differences.rejected', { n: r.surplus_qty }));
        await differences.reload();
      } catch (err) { showError(err); }
    });
  };

  return (
    <>
      <Panel
        title={t('differences.title')}
        description={t('differences.desc')}
        badge={
          <div className="field" style={{ minWidth: 180 }}>
            <label htmlFor="diffStatus">{t('differences.filter')}</label>
            <select className="select" id="diffStatus" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATES.map((s) => (
                <option key={s || 'all'} value={s}>
                  {s === 'OPEN' ? t('differences.open') : s === 'RESOLVED' ? t('differences.resolved') : t('differences.all')}
                </option>
              ))}
            </select>
          </div>
        }
      >
        {differences.data && rows.length === 0 ? <EmptyState icon="inbox" title={t('differences.none')} /> : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>{t('differences.cols.date')}</th>
                  <th>{t('differences.cols.shipment')}</th>
                  <th>{t('differences.cols.destination')}</th>
                  <th>{t('differences.cols.product')}</th>
                  <th className="num">{t('differences.cols.dispatched')}</th>
                  <th className="num">{t('differences.cols.received')}</th>
                  <th className="num">{t('differences.cols.missing')}</th>
                  <th className="num">{t('differences.cols.damaged')}</th>
                  <th className="num">{t('differences.cols.surplus')}</th>
                  <th>{t('differences.cols.user')}</th>
                  <th>{t('differences.cols.resolution')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="diffBody">
                {differences.loading && !differences.data ? <TableSkeleton cols={12} rows={3} /> : null}
                {rows.map((d) => (
                  <tr key={d.id} data-difference={d.id} data-status={d.status}>
                    <td>{formatDate(d.created_at, lang)}</td>
                    <td><b>{d.shipment_no}</b><div className="tiny muted">{d.receipt_no}</div></td>
                    <td>{d.destination_name}</td>
                    <td><b>{d.ref}</b> {d.product_name}</td>
                    <td className="num">{d.dispatched_qty}</td>
                    <td className="num">{d.received_qty}</td>
                    <td className="num">{d.missing_qty}</td>
                    <td className="num">{d.damaged_qty}</td>
                    <td className="num" data-col="surplus">{d.surplus_qty}</td>
                    <td>{d.user_name ?? ''}</td>
                    <td data-col="resolution">
                      {d.resolution ? (
                        <>
                          <Badge tone={d.resolution === 'ACCEPTED' ? 'green' : 'amber'}>{t(`differences.resolution.${d.resolution}`)}</Badge>
                          <div className="tiny muted">{[d.resolved_by_name, d.resolution_note].filter(Boolean).join(' · ')}</div>
                          {d.adjustment_no ? <div className="tiny muted">{d.adjustment_no}</div> : null}
                        </>
                      ) : d.surplus_qty > 0 ? (
                        <Badge tone="amber">{t('differences.pending')}</Badge>
                      ) : null}
                    </td>
                    <td>
                      {d.surplus_qty > 0 && d.status === 'OPEN' && can('inventory.adjust') ? (
                        <button type="button" className="btn sm black" data-resolve={d.id} onClick={() => { setTarget(d); setReason(''); }}>
                          {t('differences.resolve')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Modal
        open={!!target}
        title={t('differences.resolveTitle', { ref: target?.ref ?? '', destination: target?.destination_name ?? '' })}
        help={t('differences.resolveHelp', { n: target?.surplus_qty ?? 0 })}
        onClose={() => setTarget(null)}
      >
        <form className="formgrid" id="resolveForm" onSubmit={resolve(true)}>
          <div className="field span2">
            <label htmlFor="resolveReason">{t('differences.reason')}</label>
            <input id="resolveReason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button className="btn black" type="submit" id="resolveAccept" disabled={busy || !reason.trim()}>{t('differences.accept')}</button>
          <button type="button" className="btn" id="resolveReject" disabled={busy || !reason.trim()} onClick={(e) => resolve(false)(e)}>
            {t('differences.reject')}
          </button>
        </form>
      </Modal>
    </>
  );
}
